import _ from 'lodash';
import { gql } from '@apollo/client';
import { captureException } from '@playbookux/playbook-web-core';

export const STATUS = ['pending', 'rejected'];

export const COMPANY_FRAGMENT = gql`
  fragment CompanyFragment on Company {
    id
    balanceRemaining
    name
    plan
    balanceUsed
    stripeCustomerId
    uploadResearchTotalStorage
    uploadResearchAvailableStorage
  }
`;

export const GET_COMPANY_BY_ID = gql`
  query Company($id: ID!) {
    company(id: $id) {
      ...CompanyFragment
    }
  }
  ${COMPANY_FRAGMENT}
`;

export const CREATE_PREPAID_BALANCE = gql`
  mutation CreatePrepaidBalance(
    $companyId: ID!,
    $prepaidAmount: Int!,
    $token: String,
    $paymentMethodId: String,
    $rememberCard: Boolean!
  ) {
    createPrepaidBalance(
      companyId: $companyId,
      prepaidAmount: $prepaidAmount,
      token: $token,
      paymentMethodId: $paymentMethodId,
      rememberCard: $rememberCard,
    ) {
      company {
        ...CompanyFragment
      }
    }
  }
  ${COMPANY_FRAGMENT}
`;

export const DELETE_COMPANY = gql`
  mutation DeleteCompany($companyId: ID!) {
    deleteCompany(companyId: $companyId) {
      void
    }
  }
`;

export const UPDATE_COMPANY_NAME = gql`
  mutation UpdateCompanyMutation($companyId: ID!, $attributes: CompanyAttributes!) {
    updateCompanyMutation(companyId: $companyId, attributes: $attributes) {
      company {
        id
      }
    }
  }
`;

export const UPDATE_COMPANY_TOGGLE = gql`
  mutation UpdateCompanyInviteToggle($companyId: ID!, $toggleValue: Boolean!) {
    updateCompanyInviteToggle(companyId: $companyId, toggleValue: $toggleValue) {
      company {
        id
        inviteToggleUserId
      }
    }
  }
`;

export const CREATE_INVITE_LINK = gql`
  mutation CreateInviteLinkMutation($companyId: ID!) {
    createInviteLink(companyId: $companyId) {
      company {
        id
        inviteLink
      }
    }
  }
`;

export const CREATE_COMPANY_INVITES = gql`
  mutation CreateCompanyInvites($companyId: ID!, $emails: [String!]!) {
    createCompanyInvites(companyId: $companyId, emails: $emails) {
      companyInvites {
        id
        email
        status
        lastSend
        inviteType
      }
      companyMembers {
        id
        role
        hidden
        user{
          id
          email
        }
      }
      emailsAlreadyInvited
    }
  }
`;

export const RESEND_INVITES = gql`
  mutation ResendInvites($companyId: ID!, $emails: [String!]!) {
    resendInvites(companyId: $companyId, emails: $emails) {
      companyInvites {
        id
        email
        status
        lastSend
      }
    }
  }
`;

export const UPDATE_COMPANY_INVITES = gql`
  mutation UpdateCompanyInvite($attributes: CompanyInviteAttributes!) {
    updateCompanyInvite(attributes: $attributes) {
      companyMember {
        id
        user {
          id
          email
        }
      }
    }
  }
`;

export const GET_COMPANY_INVITES = gql`
  query CompanyInvites($companyId: ID!, $status: [CompanyInviteStatusEnum!]) {
    companyInvites(companyId: $companyId, status: $status) {
      id
      email
      status
      inviteType
      lastSend
    }
    companyMembers(companyId: $companyId, hidden: false) {
      id
      role
      projectApproval
      user {
        id
        email
        profile {
          firstName
          lastName
        }
      }
    }
  }
`;

export const GET_CURRENT_COMPANY_MEMBER = gql`
  query CompanyMembers($userId: ID!, $companyId: ID!) {
    companyMembers(userId: $userId, companyId: $companyId) {
      id
      role
      projectApproval
      user {
        profile {
          firstName
          lastName
        }
      }
    }
  }
`;

export const CREATE_BILLING_PORTAL = gql`
  mutation CreateBillingPortal($customerId: ID!) {
    createBillingPortal(customerId: $customerId) {
      portal {
        id
        url
      }
    }
  }
`;

export const GET_SUBSCRIPTIONS = gql`
  query Subscriptions($companyId: ID!) {
    subscriptions(companyId: $companyId) {
      id
      status
      trialEnd
    }
  }
`;

export const UPDATE_COMPANY_MEMBER = gql`
  mutation UpdateCompanyMembers($companyId: ID!, $targetUserId: ID!, $role: RoleTypeEnum!, $projectApproval: ProjectApprovalTypeEnum!) {
    updateCompanyMembers(companyId: $companyId, targetUserId: $targetUserId, role: $role, projectApproval: $projectApproval) {
      companyMember {
        id
        role
        projectApproval
        user{
          id
          email
        }
      }
    }
  }
`;

export const UPDATE_PROFILE = gql`
  mutation UpdateProfile($attributes: ProfileAttributes!) {
    updateProfile(attributes: $attributes) {
      profile {
        firstName
        lastName
      }
    }
  }
`;

export const DELETE_COMPANY_MEMBER = gql`
  mutation deleteCompanyMember($id: ID!) {
    deleteCompanyMember(id: $id) {
      companyMember {
        id
        role
        user {
          id
          email
        }
      }
    }
  }
`;

export const SAVED_CARDS = gql`
  query PaymentMethods($companyId: ID!) {
    paymentMethods(companyId: $companyId) {
      id
      isDefault
      card {
        brand
        last4
        expMonth
        expYear
      }
    }
  }
`;

export const CREATE_PAYMENT_METHOD = gql`
  mutation CreatePaymentMethod($companyId: ID!, $token: String!, $isDefault: Boolean) {
    createPaymentMethod(companyId: $companyId, token: $token, isDefault: $isDefault) {
      paymentMethod {
        id
        isDefault
        card {
          brand
          last4
          expMonth
          expYear
        }
      }
    }
  }
`;

export const UPDATE_DEFAULT_PAYMENT_METHOD = gql`
  mutation UpdateDefaultPaymentMethod($companyId: ID!, $paymentMethodId: String!) {
    updateDefaultPaymentMethod(companyId: $companyId, paymentMethodId: $paymentMethodId) {
      paymentMethod {
        id
        isDefault
        card {
          brand
          last4
          expMonth
          expYear
        }
      }
    }
  }
`;

export const REMOVE_PAYMENT_METHOD = gql`
  mutation RemovePaymentMethod($companyId: ID!, $paymentMethodId: String!) {
    removePaymentMethod(companyId: $companyId, paymentMethodId: $paymentMethodId) {
      paymentMethod {
        id
        isDefault
        card {
          brand
          last4
          expMonth
          expYear
        }
      }
    }
  }
`;

export const GET_COMPANY_RECEIPTS = gql`
  query Receipts($companyId: ID!) {
    receipts(companyId: $companyId) {
      id
      receiptUrl
      amount
      date
    }
  }
`;

export const GET_COMPANY_STORAGE_USAGES = gql`
  query CompanyStorageUsage($companyId: ID!) {
    companyStorageUsage(companyId: $companyId) {
      id
      createdAt
      user {
        profile {
          firstName
          lastName
        }
      }
      project {
        id
        name
      }
      consumedStorage
      testSession {
        id
        tester {
          id
          removed
          user {
            profile {
              firstName
              lastName
            }
          }
        }
      }
    }
  }
`;

export const VERIFY_CAPTCHA = gql`
  mutation VerifyCaptcha($token: String!) {
    verifyCaptcha(token: $token){
      success
    }
  }
`;

export const updatePaymentMethodRemoved = (companyId) => (cache, { data }) => {
  const { removePaymentMethod: { paymentMethod } } = data;
  const variables = { companyId };

  const { paymentMethods: cachedPaymentMethods } = cache.readQuery({
    query: SAVED_CARDS,
    variables,
  });

  const newPaymentMethods = _.filter(cachedPaymentMethods, ({ id }) => id !== paymentMethod.id);

  const paymentMethods = _.map(newPaymentMethods, (pm) => {
    const newPaymentMethod = pm;

    if (!_.find(newPaymentMethods, ({ isDefault }) => isDefault)) {
      newPaymentMethod.isDefault = newPaymentMethod.id === _.first(newPaymentMethods).id;
    }

    return { ...newPaymentMethod };
  });

  cache.writeQuery({
    query: SAVED_CARDS,
    variables,
    data: { paymentMethods },
  });
};

export const updateDefaultPaymentMethodUpdated = (companyId) => (cache, { data }) => {
  const { updateDefaultPaymentMethod: { paymentMethod: { id: paymentMethodId } } } = data;
  const variables = { companyId };

  const { paymentMethods: cachedPaymentMethods } = cache.readQuery({
    query: SAVED_CARDS,
    variables,
  });

  const paymentMethods = _.map(cachedPaymentMethods, (paymentMethod) => {
    const newPaymentMethod = { ...paymentMethod };

    // Set true if current payment method is the updated payment method
    newPaymentMethod.isDefault = paymentMethod.id === paymentMethodId;

    return newPaymentMethod;
  });

  cache.writeQuery({
    query: SAVED_CARDS,
    variables,
    data: { paymentMethods },
  });
};

export const updatePaymentMethodCreated = (companyId) => (cache, { data }) => {
  const { createPaymentMethod: { paymentMethod } } = data;

  const variables = { companyId };

  const { paymentMethods: cachedPaymentMethods } = cache.readQuery({
    query: SAVED_CARDS,
    variables,
  });

  const newPaymentMethods = [...cachedPaymentMethods, paymentMethod];

  const paymentMethods = _.map(newPaymentMethods, (pm) => {
    const newPaymentMethod = { ...pm };

    if (paymentMethod.isDefault) {
      newPaymentMethod.isDefault = newPaymentMethod.id === paymentMethod.id;
    }

    return { ...newPaymentMethod };
  });

  cache.writeQuery({
    query: SAVED_CARDS,
    variables,
    data: { paymentMethods },
  });
};

export const updateCompanyMemberDeleted = (companyId, id) => (cache) => {
  const variables = {
    companyId,
    status: STATUS,
  };

  const { companyMembers: cachedMembers, companyInvites: cachedInvites } = cache.readQuery({
    query: GET_COMPANY_INVITES,
    variables,
  });

  const companyMembers = _.filter(cachedMembers, (member) => member.id !== id);
  const companyInvites = _.filter(cachedInvites, (invite) => invite.id !== id);

  cache.writeQuery({
    query: GET_COMPANY_INVITES,
    variables,
    data: {
      companyMembers,
      companyInvites,
    },
  });
};

export const updateCompanyInviteCreated = (companyId) => (
  cache, { data: { createCompanyInvites } },
) => {
  try {
    const variables = {
      companyId,
      status: STATUS,
    };

    const {
      companyInvites: cachedCompanyInvites = [],
      companyMembers: cachedCompanyMembers = [],
    } = cache.readQuery({
      query: GET_COMPANY_INVITES,
      variables,
    });

    const companyInvites = _.uniqBy([...cachedCompanyInvites, ...createCompanyInvites.companyInvites], 'id');
    const companyMembers = _.uniqBy([...cachedCompanyMembers, ...createCompanyInvites.companyMembers], 'id');

    cache.writeQuery({
      query: GET_COMPANY_INVITES,
      variables,
      data: {
        companyInvites,
        companyMembers,
      },
    });
  } catch (e) {
    captureException('UPDATE_COMPANY_INVITE_CREATED', e);
  }
};

export const GET_USAGE_PROJECTS = gql`
  query Projects(
    $companyId: ID!,
    $includeDeletedProjects: Boolean
    $limit: Int,
    $offset: Int,
    $order: String,
    ) {
    projects(
      companyId: $companyId,
      includeDeletedProjects: $includeDeletedProjects,
      limit: $limit,
      offset: $offset,
      order: $order,
      ) {
      id
      name
      createdAt
      createdBy {
        profile {
          firstName
          lastName
        }
      }
      testType
      status
      participantsSource
      costProject
    }
  }
`;

export const GET_USAGE_LOGS = gql`
  query UsageLogs(
    $limit: Int,
    $offset: Int,
    $order: String,
    ) {
    usageLogs(
      limit: $limit,
      offset: $offset,
      order: $order,
    ) {
      id
      project {
        name
        testType
        participantsSource
        movedFrom
        status
        createdBy {
          profile {
            firstName
            lastName
          }
        }
      }
      price
      reason
      hidden
      createdAt
    }
  }
`;

export const GET_USAGE_LOGS_BY_COMPANY = gql`
  query UsageLogsByCompany(
    $companyId: ID!,
    $limit: Int,
    $offset: Int,
    $order: String
    ) {
    usageLogsByCompany(
      companyId: $companyId,
      limit: $limit,
      offset: $offset,
      order: $order,
    ) {
      id
      project {
        name
        testType
        participantsSource
        deletedAt
        sessionDuration
        movedTo
        createdBy {
          profile {
            firstName
            lastName
            email
          }
        }
      }
      price
      reason
      hidden
      createdAt
    }
  }
`;

export const TOGGLE_HIDE_USAGE_LOG = gql`
  mutation ToggleHideUsageLog($id: ID!) {
    toggleHideUsageLog(id: $id) {
      usageLog {
        id
        hidden
      }
    }
  }
`;

export const PURCHASE_STORAGE = gql`
  mutation PurchaseStorage($input: PurchaseStorageInput!) {
    purchaseStorage(input: $input) {
      company {
        id
        uploadResearchTotalStorage
      }
    }
  }
`;
